import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Upload, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    benefits: ['', '', '', ''],
    price: 299,
    images: ['', '', '', ''],
    icon: '🌿',
    category: 'wellness'
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/?active_only=false`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/products/upload-image`, formDataUpload);
      
      const newImages = [...formData.images];
      newImages[index] = response.data.url;
      setFormData({ ...formData, images: newImages });
      
      toast.success(`Image ${index + 1} uploaded!`);
    } catch (error) {
      toast.error('Failed to upload image');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.name || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Filter out empty benefits and images
    const cleanedData = {
      ...formData,
      benefits: formData.benefits.filter(b => b.trim() !== ''),
      images: formData.images.filter(img => img.trim() !== '')
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, cleanedData);
        toast.success('Product updated successfully!');
      } else {
        await axios.post(`${API}/products`, cleanedData);
        toast.success('Product created successfully!');
      }
      
      fetchProducts();
      closeModal();
    } catch (error) {
      toast.error('Failed to save product');
      console.error(error);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
      console.error(error);
    }
  };

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description,
        benefits: [...product.benefits, '', '', '', ''].slice(0, 4),
        price: product.price,
        images: [...product.images, '', '', '', ''].slice(0, 4),
        icon: product.icon || '🌿',
        category: product.category || 'wellness'
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        benefits: ['', '', '', ''],
        price: 299,
        images: ['', '', '', ''],
        icon: '🌿',
        category: 'wellness'
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  const handleBenefitChange = (index, value) => {
    const newBenefits = [...formData.benefits];
    newBenefits[index] = value;
    setFormData({ ...formData, benefits: newBenefits });
  };

  const handleImageUrlChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({ ...formData, images: newImages });
  };

  return (
    <div className="admin-container">
      <div className="container">
        <div className="admin-header">
          <div>
            <h1 className="admin-title">Product Management</h1>
            <p className="admin-subtitle">Manage your Sole to Soul products</p>
          </div>
          <button className="btn-primary" onClick={() => openModal()}>
            <Plus size={20} />
            Add Product
          </button>
        </div>

        {loading ? (
          <div className="loading-state">Loading products...</div>
        ) : (
          <div className="admin-products-grid">
            {products.map((product) => (
              <Card key={product.id} className={`admin-product-card ${!product.is_active ? 'inactive' : ''}`}>
                <CardContent className="admin-product-content">
                  <div className="product-images-preview">
                    {product.images.slice(0, 2).map((img, idx) => (
                      <img key={idx} src={img} alt={product.name} className="preview-image" />
                    ))}
                  </div>
                  <div className="admin-product-info">
                    <h3 className="admin-product-name">
                      <span className="product-icon-large">{product.icon}</span>
                      {product.name}
                    </h3>
                    <p className="admin-product-desc">{product.description}</p>
                    <div className="admin-product-meta">
                      <span className="product-price">₹{product.price}</span>
                      <span className="product-category">{product.category}</span>
                      {!product.is_active && <span className="inactive-badge">Inactive</span>}
                    </div>
                  </div>
                  <div className="admin-product-actions">
                    <button className="btn-icon" onClick={() => openModal(product)}>
                      <Edit size={18} />
                    </button>
                    <button className="btn-icon delete" onClick={() => handleDelete(product.id)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Icon</label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="🌿"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of benefits"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price (₹) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="wellness">Wellness</option>
                    <option value="outdoor">Outdoor</option>
                    <option value="seasonal">Seasonal</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Benefits (up to 4)</label>
                {formData.benefits.map((benefit, index) => (
                  <input
                    key={index}
                    type="text"
                    value={benefit}
                    onChange={(e) => handleBenefitChange(index, e.target.value)}
                    placeholder={`Benefit ${index + 1}`}
                    className="benefit-input"
                  />
                ))}
              </div>

              <div className="form-group">
                <label>Product Images (4 images required)</label>
                <div className="images-grid">
                  {[0, 1, 2, 3].map((index) => (
                    <div key={index} className="image-upload-box">
                      {formData.images[index] ? (
                        <div className="image-preview">
                          <img src={formData.images[index]} alt={`Preview ${index + 1}`} />
                          <button
                            type="button"
                            className="remove-image"
                            onClick={() => handleImageUrlChange(index, '')}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <label className="upload-label">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(index, e.target.files[0])}
                            style={{ display: 'none' }}
                          />
                          <Upload size={24} />
                          <span>Upload Image {index + 1}</span>
                        </label>
                      )}
                      <input
                        type="text"
                        value={formData.images[index]}
                        onChange={(e) => handleImageUrlChange(index, e.target.value)}
                        placeholder="Or paste image URL"
                        className="image-url-input"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsAdmin;